// Copyright 2026 The Simlin Authors. All rights reserved.
// Use of this source code is governed by the Apache License,
// Version 2.0, that can be found in the LICENSE file.

/**
 * D3 acceptance criterion 32 -- overlay visibility state.
 *
 * Independent boolean flags governing whether each of the four
 * overlay kinds (pattern groups, variable highlights, annotation
 * labels, free-floating annotations) paints on the canvas.
 *
 * Visibility lives entirely in React state.  Toggling a flag:
 *
 *   - re-renders Canvas with the updated visibility prop, which
 *     short-circuits the corresponding overlay render block.
 *   - does NOT touch `view.elements` (the model's diagram layout
 *     is unaffected).
 *   - does NOT touch `Project` / `Model` / `Sim` (engine state and
 *     simulation results are unaffected).
 *   - does NOT trigger the patch pipeline (no `setView`,
 *     `queueViewUpdate`, or `updateProject` call).
 *
 * These guarantees are mechanical: this file imports nothing from
 * `@simlin/engine` or `@simlin/core/datamodel`, and the OverlayToggle
 * UI plus the Canvas gating site enforce the same.  All three are
 * audited by [tests/overlay-toggle-readonly.test.ts](../tests/overlay-toggle-readonly.test.ts).
 */

/**
 * Map of overlay kind to visibility flag.  Field names match the
 * corresponding optional Canvas props (`patternGroups`,
 * `variableHighlights`, `annotationLabels`, `annotations`) so the
 * gating code in Canvas reads naturally:
 *
 *   `visibility.patternGroups`  -> show the C31 pattern boundary
 *                                  overlay
 *   `visibility.variableHighlights` -> show the C29 per-variable
 *                                  emphasis overlay
 *   `visibility.annotationLabels` -> show the C30 pill text-badge
 *                                  overlay
 *   `visibility.annotations` -> show the C28 free-floating text /
 *                               box annotation overlay
 */
export interface OverlayVisibility {
  readonly patternGroups: boolean;
  readonly variableHighlights: boolean;
  readonly annotationLabels: boolean;
  readonly annotations: boolean;
}

export type OverlayKind = keyof OverlayVisibility;

/**
 * Default visibility -- all four overlays start visible.  Callers
 * that don't supply an `OverlayVisibility` to Canvas behave as if
 * every flag is `true`, so existing tests and call sites continue
 * to render every overlay they pass data for.
 */
export const DEFAULT_OVERLAY_VISIBILITY: OverlayVisibility = {
  patternGroups: true,
  variableHighlights: true,
  annotationLabels: true,
  annotations: true,
};

/**
 * Return a new `OverlayVisibility` with exactly one flag flipped.
 * Pure: never mutates `current`.  Use this in setState callbacks
 * to keep the visibility state immutable.
 */
export function withOverlayToggled(
  current: OverlayVisibility,
  kind: OverlayKind,
): OverlayVisibility {
  return { ...current, [kind]: !current[kind] };
}

/**
 * Convenience predicate for the Canvas gating sites.  Treats an
 * absent `visibility` (undefined) as "all overlays visible" so
 * callers that have not adopted the toggle continue to render
 * everything.
 */
export function isOverlayVisible(
  visibility: OverlayVisibility | undefined,
  kind: OverlayKind,
): boolean {
  return visibility === undefined || visibility[kind];
}
